import YAML from 'yaml';
import type { ParsedNode, ParsedEdge, DeclaredInput, NodeRegistry, HandleDefinition, WrapperSource } from '../types';

type RawNode = {
  type: string;
  id?: string;
  values?: Record<string, unknown>;
  inputsConfig?: Record<string, unknown>;
  /** A wrapper's nested source (e.g. `Collect`'s query). Read here only to be carried. */
  source?: WrapperSource;
  edges?: Array<{
    to: string;
    fromOutput?: string;
    toInput?: string;
    optional?: boolean;
  }>;
};

type RawDsl = {
  nodes: RawNode[];
  declaredInputs?: Array<{ nodeId: string; inputName: string }>;
};

/**
 * Assign unique IDs to nodes. When multiple nodes share the same type and
 * have no explicit id, generate IDs like `{type}`, `{type}-1`, `{type}-2`.
 * The first occurrence keeps the bare type name.
 */
function assignUniqueIds( rawNodes: RawNode[]): Array<RawNode & { id: string }> {
  // First pass: assign preliminary ids (explicit id or type)
  const preliminary = rawNodes.map(( n ) => ({
    ...n,
    id: n.id || n.type
  }));

  // Count occurrences of each preliminary id
  const idOccurrences = new Map<string, number>();
  for ( const n of preliminary ) {
    idOccurrences.set( n.id, ( idOccurrences.get( n.id ) || 0 ) + 1 );
  }

  // Second pass: deduplicate ids that appear more than once
  const seenCount = new Map<string, number>();
  for ( const n of preliminary ) {
    const baseId = n.id;
    const totalOccurrences = idOccurrences.get( baseId ) || 1;

    if ( totalOccurrences > 1 ) {
      const seen = seenCount.get( baseId ) || 0;
      if ( seen === 0 ) {
        // First occurrence keeps bare name
        n.id = baseId;
      } else {
        n.id = `${baseId}-${seen}`;
      }
      seenCount.set( baseId, seen + 1 );
    }
  }

  return preliminary as Array<RawNode & { id: string }>;
}

/**
 * Resolve an edge target reference to a concrete node ID.
 * First checks by node id, then by node type.
 */
function buildResolver( nodes: Array<{ id: string; type: string }> ): ( to: string ) => string {
  return ( to: string ): string => {
    const foundById = nodes.find(( n ) => n.id === to );
    if ( foundById ) return foundById.id;
    const foundByType = nodes.find(( n ) => n.type === to );
    if ( foundByType ) return foundByType.id;
    throw new Error( `Edge target not found: ${to}` );
  };
}

/**
 * Look up inputs and outputs for a node type from the registry.
 * Falls back to inferring from edges when the type is not in the registry.
 */
function resolveHandles(
  nodeId: string,
  nodeType: string,
  registry: NodeRegistry,
  edgeInputs: Set<string>,
  edgeOutputs: Set<string>
): { inputs: HandleDefinition[]; outputs: HandleDefinition[] } {
  const entry = registry[nodeType];

  if ( entry ) {
    return {
      inputs: entry.inputs.map(( h ) => ({ ...h })),
      outputs: entry.outputs.map(( h ) => ({ ...h }))
    };
  }

  // Fallback: infer from edges
  const inputs: HandleDefinition[] = Array.from( edgeInputs ).map(( name ) => ({
    name,
    type: 'any' as const
  }));
  const outputs: HandleDefinition[] = Array.from( edgeOutputs ).map(( name ) => ({
    name,
    type: 'any' as const
  }));

  return { inputs, outputs };
}

/**
 * Parse a YAML DSL string (or pre-parsed object) into ParsedNodes, ParsedEdges,
 * and DeclaredInputs.
 *
 * Uses the NodeRegistry to resolve the full set of typed inputs/outputs for each
 * node. Falls back to edge-based inference for node types not in the registry.
 */
export function parseDsl(
  yaml: string,
  registry: NodeRegistry
): { nodes: ParsedNode[]; edges: ParsedEdge[]; declaredInputs: DeclaredInput[] } {
  const dsl: RawDsl = typeof yaml === 'string' ? YAML.parse( yaml ) : yaml;

  // Assign unique IDs with deduplication
  const rawNodes = assignUniqueIds( dsl.nodes || []);

  // Build edge target resolver
  const resolveTo = buildResolver( rawNodes );

  // Build a map from node id to type for handle resolution
  const nodeIdToType = new Map<string, string>();
  for ( const n of rawNodes ) {
    nodeIdToType.set( n.id, n.type );
  }

  // Build edges
  const edges: ParsedEdge[] = [];
  for ( const n of rawNodes ) {
    for ( const e of n.edges ?? []) {
      const targetId = resolveTo( e.to );

      // Resolve omitted handles to the default (first) handle from the registry.
      // For nodes with dynamic handles (0 handles in registry), leave undefined.
      let sourceHandle = e.fromOutput;
      if ( !sourceHandle ) {
        const sourceEntry = registry[n.type];
        if ( sourceEntry && sourceEntry.outputs.length > 0 ) {
          sourceHandle = sourceEntry.outputs[0].name;
        }
      }

      let targetHandle = e.toInput;
      if ( !targetHandle ) {
        const targetType = nodeIdToType.get( targetId );
        const targetEntry = targetType ? registry[targetType] : undefined;
        if ( targetEntry && targetEntry.inputs.length > 0 ) {
          targetHandle = targetEntry.inputs[0].name;
        }
      }

      const edgeId = `${n.id}-${sourceHandle ?? 'out'}-${targetId}-${targetHandle ?? 'in'}`;

      edges.push({
        id: edgeId,
        source: n.id,
        target: targetId,
        sourceHandle,
        targetHandle,
        ...( e.optional ? { optional: true } : {})
      });
    }
  }

  // Collect edge-inferred inputs/outputs per node (used as fallback)
  const edgeInputsMap = new Map<string, Set<string>>();
  const edgeOutputsMap = new Map<string, Set<string>>();
  for ( const n of rawNodes ) {
    edgeInputsMap.set( n.id, new Set());
    edgeOutputsMap.set( n.id, new Set());
  }
  for ( const e of edges ) {
    if ( e.targetHandle ) edgeInputsMap.get( e.target )?.add( e.targetHandle );
    else edgeInputsMap.get( e.target )?.add( 'input' );
    if ( e.sourceHandle ) edgeOutputsMap.get( e.source )?.add( e.sourceHandle );
    else edgeOutputsMap.get( e.source )?.add( 'output' );
  }

  // Build ParsedNodes with registry-aware handles
  const nodes: ParsedNode[] = rawNodes.map(( n ) => {
    const { inputs, outputs } = resolveHandles(
      n.id,
      n.type,
      registry,
      edgeInputsMap.get( n.id ) || new Set(),
      edgeOutputsMap.get( n.id ) || new Set()
    );

    const node: ParsedNode = {
      id: n.id,
      type: n.type,
      inputs,
      outputs
    };

    if ( n.values && Object.keys( n.values ).length > 0 ) {
      node.values = n.values;
    }

    if ( n.inputsConfig && Object.keys( n.inputsConfig ).length > 0 ) {
      node.inputsConfig = n.inputsConfig;
    }

    // Carried verbatim — a wrapper whose `source` is dropped has nothing to drain.
    if ( n.source ) {
      node.source = n.source;
    }

    return node;
  });

  // Parse declaredInputs
  const declaredInputs: DeclaredInput[] = ( dsl.declaredInputs || []).map(( di ) => ({
    nodeId: di.nodeId,
    inputName: di.inputName
  }));

  return { nodes, edges, declaredInputs };
}
